import React, { useState, useEffect, useRef } from "react";
import {
  Modal, Box, Button, IconButton, Checkbox, CircularProgress,
  TextField, InputAdornment, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Popover,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LabelIcon from "@mui/icons-material/Label";
import { TEAL, TEAL_LIGHT, GREEN, GREEN_DARK, thStyle } from "../constants";
import { BootstrapButton } from "./BootstrapButton";
 
const API = process.env.REACT_APP_API_URL;
 
function useServerDropdown(endpoint, authHeadersFn, enabled) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dSearch, setDSearch] = useState("");
  const timerRef = useRef(null);
  const fetchedOnceRef = useRef(false);
  const SIZE = 30;
 
  useEffect(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { setDSearch(search); setPage(1); }, 400);
    return () => clearTimeout(timerRef.current);
  }, [search]);
 
  useEffect(() => {
    if (!enabled) { fetchedOnceRef.current = false; return; }
    if (!endpoint) return;
    setLoading(true);
    const q = dSearch
      ? `&column_name=name&column_operator=LIKE&column_value=${encodeURIComponent(dSearch)}`
      : "";
    fetch(
      `${API}${endpoint}?page=${page}&size=${SIZE}&sort_by=name&sort_order=asc${q}`,
      { headers: authHeadersFn() }
    )
      .then(r => r.json())
      .then(r => {
        setItems(r?.data?.map(i => ({ id: String(i.id), name: i.name })) || []);
        setTotal(r?.pagination_info?.total || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [endpoint, dSearch, page, enabled]);
 
  const reset = () => { setSearch(""); setPage(1); };
  return { items, search, setSearch, page, setPage, total, loading, SIZE, reset };
}

function useCategoryScopedBrandDropdown(authHeadersFn, enabled, categoryIds) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dSearch, setDSearch] = useState("");
  const timerRef = useRef(null);
  const SIZE = 30;
 
  const categoryKey = categoryIds.join(",");
 
  useEffect(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { setDSearch(search); setPage(1); }, 400);
    return () => clearTimeout(timerRef.current);
  }, [search]);
 
  useEffect(() => { setPage(1); }, [categoryKey]);
 
  useEffect(() => {
    if (!enabled) return;
    if (categoryIds.length === 0) {
      setItems([]); setTotal(0);
      return;
    }
    setLoading(true);
    const params = new URLSearchParams({
      page, size: SIZE,
      sort_by: "brand_master", sort_order: "asc",
      category_master_id: categoryIds.join(","),
    });
    if (dSearch.trim()) {
      params.append("column_operator", "LIKE");
      params.append("column_value", dSearch.trim());
    }
    fetch(`${API}/api/v1/brand-category-master?${params}`, { headers: authHeadersFn() })
      .then(r => r.json())
      .then(r => {
        const rows = r?.data || [];
        const seen = new Set();
        const deduped = [];
        rows.forEach(row => {
          const id = String(row.brand_master_id);
          if (seen.has(id)) return;
          seen.add(id);
          deduped.push({ id, name: row.brand_master });
        });
        setItems(deduped);
        setTotal(r?.pagination_info?.total || rows.length);
      })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [authHeadersFn, enabled, page, dSearch, categoryKey]); // eslint-disable-line react-hooks/exhaustive-deps
 
  const reset = () => { setSearch(""); setPage(1); };
  return { items, search, setSearch, page, setPage, total, loading, SIZE, reset };
}
 
function SingleSelectDropdown({
  label, icon, accentColor = TEAL,
  items, selected, onSelect, onClear,
  search, onSearchChange,
  page, onPageChange, total, pageSize, loadingItems,
  disabled = false, disabledHint = "",
}) {
  const [anchor, setAnchor] = useState(null);
  const totalPages = Math.ceil(total / pageSize) || 1;
 
  return (
    <>
      <Button
        onClick={e => !disabled && setAnchor(e.currentTarget)}
        variant="outlined"
        size="small"
        disabled={disabled}
        title={disabled ? disabledHint : undefined}
        endIcon={
          selected
            ? <span style={{
                background: accentColor, color: "#fff", borderRadius: 10,
                fontSize: 10, fontWeight: 700, padding: "0 6px",
                lineHeight: "16px", marginLeft: 2,
              }}>1</span>
            : <ExpandMoreIcon sx={{ fontSize: 15 }} />
        }
        sx={{
          textTransform: "none", fontSize: 12, height: 34, px: 1.5,
          borderColor: selected ? accentColor : "#ddd",
          color: selected ? accentColor : "#666",
          backgroundColor: selected ? `${accentColor}0d` : "#fff",
          fontWeight: selected ? 600 : 400,
          "&:hover": { borderColor: accentColor, backgroundColor: `${accentColor}0d` },
          whiteSpace: "nowrap", gap: 0.5,
        }}
      >
        {icon} {label}
      </Button>
 
      <Popover
        open={Boolean(anchor)} anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ elevation: 4, sx: { borderRadius: 2, width: 280, mt: 0.5 } }}
      >
        <div style={{ padding: "8px 10px", borderBottom: "1px solid #f0f0f0" }}>
          <TextField
            size="small" fullWidth autoFocus placeholder="Cari..."
            value={search} onChange={e => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {loadingItems
                    ? <CircularProgress size={13} sx={{ color: "#ccc" }} />
                    : <SearchIcon sx={{ fontSize: 14, color: "#ccc" }} />}
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => onSearchChange("")} sx={{ p: 0.2 }}>
                    <CloseIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              "& .MuiInputBase-input": { fontSize: 12, py: 0.6 },
              "& fieldset": { border: "none" },
              "& .MuiOutlinedInput-root": { background: "#f8f8f8", borderRadius: 1.5 },
            }}
          />
        </div>
 
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "4px 12px", borderBottom: "1px solid #f5f5f5", background: "#fafafa",
        }}>
          <span style={{ fontSize: 11, color: "#aaa" }}>
            {selected
              ? <span style={{ color: accentColor, fontWeight: 600 }}>1 dipilih</span>
              : `${total} item`}
          </span>
          {selected && (
            <span
              onClick={() => { onClear(); setAnchor(null); }}
              style={{ fontSize: 11, color: "#e53935", cursor: "pointer", textDecoration: "underline" }}
            >
              Reset
            </span>
          )}
        </div>
 
        <div style={{ maxHeight: 220, overflowY: "auto" }}>
          {items.length === 0 ? (
            <div style={{ padding: 14, textAlign: "center", fontSize: 12, color: "#ccc" }}>
              {search ? "Tidak ditemukan" : loadingItems ? "Memuat..." : "Belum ada data"}
            </div>
          ) : items.map(item => {
            const checked = selected === item.id;
            return (
              <div
                key={item.id}
                onClick={() => { onSelect(checked ? null : item); setAnchor(null); }}
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  padding: "6px 12px", cursor: "pointer",
                  background: checked ? `${accentColor}0f` : undefined,
                  borderLeft: checked ? `3px solid ${accentColor}` : "3px solid transparent",
                }}
              >
                <span style={{ fontSize: 12, color: checked ? accentColor : "#444", fontWeight: checked ? 600 : 400, flex: 1 }}>
                  {item.name}
                </span>
                {checked && <span style={{ fontSize: 12, color: accentColor }}>✓</span>}
              </div>
            );
          })}
        </div>
 
        {totalPages > 1 && (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "6px 10px", borderTop: "1px solid #f0f0f0", background: "#fafafa",
          }}>
            <button
              onClick={() => onPageChange(page - 1)} disabled={page <= 1 || loadingItems}
              style={{
                border: `1px solid ${page <= 1 ? "#eee" : "#d1d5db"}`,
                borderRadius: 6, padding: "4px 10px", fontSize: 12,
                background: page <= 1 ? "#f9fafb" : "#fff",
                cursor: page <= 1 ? "default" : "pointer",
                color: page <= 1 ? "#ccc" : "#374151", fontWeight: 500,
              }}
            >← Prev</button>
            <span style={{ fontSize: 11, color: "#9ca3af" }}>{page} / {totalPages}</span>
            <button
              onClick={() => onPageChange(page + 1)} disabled={page >= totalPages || loadingItems}
              style={{
                border: `1px solid ${page >= totalPages ? "#eee" : "#d1d5db"}`,
                borderRadius: 6, padding: "4px 10px", fontSize: 12,
                background: page >= totalPages ? "#f9fafb" : "#fff",
                cursor: page >= totalPages ? "default" : "pointer",
                color: page >= totalPages ? "#ccc" : "#374151", fontWeight: 500,
              }}
            >Next →</button>
          </div>
        )}
      </Popover>
    </>
  );
}
 
export default function AddProductModal({ open, onClose, onSubmit, lineName, catalogId, authHeaders }) {
  const [catalogCategoryIds, setCatalogCategoryIds] = useState([]);
  const [categoriesReady, setCategoriesReady] = useState(false);
  const loadedCatalogIdRef = useRef(null);
 
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [searchVal, setSearchVal] = useState("");
 
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
 
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [selectedMap, setSelectedMap] = useState({});
 
  const [submitting, setSubmitting] = useState(false);
 
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchTimer = useRef(null);
  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setDebouncedSearch(searchVal); setPage(0); }, 400);
    return () => clearTimeout(searchTimer.current);
  }, [searchVal]);
 
  const brandDropdown = useCategoryScopedBrandDropdown(authHeaders, open && categoriesReady, catalogCategoryIds);
 
  useEffect(() => {
    if (!open) {
      loadedCatalogIdRef.current = null;
      setCategoriesReady(false);
      return;
    }
    if (!catalogId) { setCatalogCategoryIds([]); setCategoriesReady(true); return; }
    if (loadedCatalogIdRef.current === catalogId) return;
 
    loadedCatalogIdRef.current = catalogId;
    setCategoriesReady(false);
    fetch(`${API}/api/v1/catalog-report/reports/${catalogId}/categories`, { headers: authHeaders() })
      .then(r => r.json())
      .then(r => setCatalogCategoryIds(r?.data?.categories || []))
      .catch(() => setCatalogCategoryIds([]))
      .finally(() => setCategoriesReady(true));
  }, [open, catalogId, authHeaders]);
 
  useEffect(() => {
    setSelectedBrand(null);
  }, [catalogCategoryIds.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps
 
  useEffect(() => { setPage(0); }, [selectedBrand]);
 
  useEffect(() => {
    if (!open || !categoriesReady) return;
 
    setLoading(true);
    const params = new URLSearchParams({
      page: page + 1,
      size: rowsPerPage,
      sort_by: "product_name",
      sort_order: "asc",
    });
 
    if (catalogCategoryIds.length > 0) {
      params.append("category_master_id", catalogCategoryIds.join(","));
    }
    if (selectedBrand) params.append("brand_master_id", selectedBrand.id);
 
    if (debouncedSearch.trim()) {
      params.append("column_name", "product_name");
      params.append("column_operator", "LIKE");
      params.append("column_value", debouncedSearch.trim());
    }
 
    fetch(`${API}/api/v1/product-master?${params}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(r => {
        setProducts(r?.data || []);
        setTotal(r?.pagination_info?.total || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, categoriesReady, page, rowsPerPage, selectedBrand, debouncedSearch, catalogCategoryIds]);
 
  const toggleProduct = p => {
    setSelectedIds(prev => { const n = new Set(prev); n.has(p.id) ? n.delete(p.id) : n.add(p.id); return n; });
    setSelectedMap(prev => { const n = { ...prev }; n[p.id] ? delete n[p.id] : (n[p.id] = p); return n; });
  };
 
  const toggleSelectPage = checked => {
    setSelectedIds(prev => { const n = new Set(prev); products.forEach(p => checked ? n.add(p.id) : n.delete(p.id)); return n; });
    setSelectedMap(prev => {
      const n = { ...prev };
      products.forEach(p => { if (checked) { n[p.id] = p; } else { delete n[p.id]; } });
      return n;
    });
  };
 
  const resetFilters = () => {
    setSelectedBrand(null);
    setSearchVal(""); setPage(0);
    brandDropdown.reset();
  };
 
  const handleClose = () => {
    resetFilters();
    setSelectedIds(new Set()); setSelectedMap({});
    setSubmitting(false);
    setLoading(true);
    onClose();
  };
 
  const handleSubmit = async () => {
    if (selectedIds.size === 0) return;
    setSubmitting(true);
    try {
      await onSubmit([...selectedIds].map(String));
      handleClose();
    } catch { setSubmitting(false); }
  };
 
  const allPageChecked = products.length > 0 && products.every(p => selectedIds.has(p.id));
  const somePageChecked = products.some(p => selectedIds.has(p.id)) && !allPageChecked;
  const activeFilterCount = (selectedBrand ? 1 : 0) + (debouncedSearch.trim() ? 1 : 0);
  const showLoading = loading || !categoriesReady;
  const brandDisabled = categoriesReady && catalogCategoryIds.length === 0;
 
  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={{
        position: "absolute", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        width: "95vw", height: "90vh",
        bgcolor: "background.paper", borderRadius: 3,
        boxShadow: 24, outline: "none",
        display: "flex", flexDirection: "column", overflow: "hidden",
      }}>
 
        <div style={{
          padding: "14px 22px", borderBottom: "1px solid #f0f0f0",
          flexShrink: 0, display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#1a1a1a" }}>Tambah Produk</div>
            <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>
              ke baris: <strong style={{ color: "#555" }}>{lineName}</strong>
            </div>
          </div>
          <IconButton size="small" onClick={handleClose} sx={{ color: "#bbb" }}><CloseIcon /></IconButton>
        </div>
 
        <div style={{ padding: "10px 22px", borderBottom: "1px solid #f0f0f0", flexShrink: 0, background: "#fafafa" }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
 
            <span style={{ fontSize: 11, color: "#aaa", display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
              <FilterListIcon sx={{ fontSize: 14 }} /> Filter:
            </span>
 
            <SingleSelectDropdown
              label="Brand"
              icon={<LabelIcon sx={{ fontSize: 13, mr: 0.3, verticalAlign: "middle" }} />}
              accentColor="#7c3aed"
              items={brandDropdown.items}
              selected={selectedBrand?.id || null}
              onSelect={item => { setSelectedBrand(item); setPage(0); }}
              onClear={() => { setSelectedBrand(null); setPage(0); }}
              search={brandDropdown.search} onSearchChange={brandDropdown.setSearch}
              page={brandDropdown.page} onPageChange={brandDropdown.setPage}
              total={brandDropdown.total} pageSize={brandDropdown.SIZE} loadingItems={brandDropdown.loading}
              disabled={brandDisabled}
              disabledHint="Catalog ini belum punya category, brand tidak bisa difilter"
            />
 
            <div style={{ width: 1, height: 24, background: "#e0e0e0", flexShrink: 0 }} />
 
            <div style={{
              display: "flex", alignItems: "center",
              border: "1px solid #ddd", borderRadius: 6,
              background: "#fff", overflow: "hidden", height: 34,
            }}>
              <TextField
                size="small"
                placeholder="Cari nama produk…"
                value={searchVal}
                onChange={e => setSearchVal(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 15, color: "#ccc" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchVal ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchVal("")} sx={{ p: 0.3 }}>
                        <CloseIcon sx={{ fontSize: 13 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{
                  width: 280,
                  "& .MuiInputBase-input": { fontSize: 12, py: 0.7 },
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                }}
              />
            </div>
 
            {activeFilterCount > 0 && (
              <Button size="small" onClick={resetFilters}
                sx={{
                  textTransform: "none", fontSize: 11, color: "#e53935",
                  minWidth: "unset", p: "2px 8px",
                  border: "1px solid #ffcdd2", borderRadius: 5,
                }}>
                Reset ({activeFilterCount})
              </Button>
            )}
          </div>
 
          {selectedBrand && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8 }}>
              <Chip
                label={selectedBrand.name} size="small"
                onDelete={() => { setSelectedBrand(null); setPage(0); }}
                sx={{ fontSize: 11, height: 22, backgroundColor: "#7c3aed18", color: "#7c3aed", "& .MuiChip-deleteIcon": { color: "#7c3aed", fontSize: 14 } }}
              />
            </div>
          )}
        </div>
 
        <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
 
          <div style={{
            padding: "6px 22px", borderBottom: "1px solid #f5f5f5", flexShrink: 0,
            display: "flex", alignItems: "center", gap: 8,
            background: (somePageChecked || allPageChecked) ? `${TEAL}08` : "#fff",
          }}>
            <Checkbox
              size="small" checked={allPageChecked} indeterminate={somePageChecked}
              onChange={e => toggleSelectPage(e.target.checked)}
              sx={{ p: 0.3, color: "#ccc", "&.Mui-checked": { color: TEAL }, "&.MuiCheckbox-indeterminate": { color: TEAL } }}
            />
            <span style={{ fontSize: 12, color: "#888" }}>
              {allPageChecked
                ? `Semua ${products.length} di halaman ini dipilih`
                : somePageChecked
                ? `${products.filter(p => selectedIds.has(p.id)).length} di halaman ini dipilih`
                : `Pilih semua di halaman ini (${products.length})`}
            </span>
            <span style={{ marginLeft: "auto", fontSize: 12, color: "#bbb" }}>
              {!showLoading && total > 0 && `${total.toLocaleString()} produk ditemukan`}
            </span>
          </div>
 
          <TableContainer sx={{ flex: 1, overflowY: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell style={thStyle} align="center" width={48} />
                  <TableCell style={thStyle} width={120}>Kode</TableCell>
                  <TableCell style={thStyle}>Nama Produk</TableCell>
                  <TableCell style={thStyle} width={200}>Kategori</TableCell>
                  <TableCell style={thStyle} width={160}>Brand</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {showLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={28} sx={{ color: TEAL }} />
                    </TableCell>
                  </TableRow>
                ) : products.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 8, color: "#bbb" }}>
                      <div style={{ fontSize: 14 }}>Tidak ada produk ditemukan</div>
                      <div style={{ fontSize: 12, marginTop: 4 }}>Coba ubah filter atau kata kunci pencarian</div>
                    </TableCell>
                  </TableRow>
                ) : products.map(p => {
                  const checked = selectedIds.has(p.id);
                  return (
                    <TableRow
                      key={p.id} hover onClick={() => toggleProduct(p)}
                      sx={{
                        cursor: "pointer",
                        backgroundColor: checked ? `${TEAL_LIGHT}66` : undefined,
                        transition: "background .1s",
                      }}
                    >
                      <TableCell align="center" sx={{ py: 0.5 }}>
                        <Checkbox
                          size="small" checked={checked}
                          onChange={() => toggleProduct(p)} onClick={e => e.stopPropagation()}
                          sx={{ p: 0.3, color: "#ccc", "&.Mui-checked": { color: TEAL } }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: 11, color: "#999", py: 0.6 }}>
                        {p.product_id || "—"}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13, py: 0.6, fontWeight: checked ? 600 : 400, color: checked ? TEAL : "#222" }}>
                        {p.product_name || "—"}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, color: "#777", py: 0.6 }}>{p.category_master || "—"}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: "#777", py: 0.6 }}>{p.brand_master || "—"}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
 
          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            sx={{ borderTop: "1px solid #f0f0f0", flexShrink: 0 }}
          />
        </div>
 
        <div style={{
          padding: "12px 22px", borderTop: "1px solid #f0f0f0", flexShrink: 0,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          background: "#fff",
        }}>
          <div>
            {selectedIds.size > 0 ? (
              <div style={{
                display: "flex", alignItems: "center", gap: 6,
                background: `${GREEN}12`, border: `1px solid ${GREEN}44`,
                borderRadius: 8, padding: "4px 12px",
              }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 15, color: GREEN }} />
                <span style={{ fontSize: 12, fontWeight: 700, color: GREEN }}>
                  {selectedIds.size} produk dipilih
                </span>
              </div>
            ) : (
              <span style={{ fontSize: 12, color: "#ccc" }}>Centang produk yang ingin ditambahkan</span>
            )}
          </div>
 
          <div style={{ display: "flex", gap: 8 }}>
            <Button variant="outlined" onClick={handleClose}
              sx={{ textTransform: "none", color: "#999", borderColor: "#ddd", fontSize: 13 }}>
              Batal
            </Button>
            <BootstrapButton
              onClick={handleSubmit}
              disabled={submitting || selectedIds.size === 0}
              sx={{
                minWidth: 180,
                backgroundColor: selectedIds.size > 0 ? GREEN : undefined,
                "&:hover": { backgroundColor: selectedIds.size > 0 ? GREEN_DARK : undefined },
              }}
            >
              {submitting
                ? <><CircularProgress size={14} sx={{ color: "#fff", mr: 1 }} /> Menyimpan...</>
                : `+ Tambahkan${selectedIds.size > 0 ? ` (${selectedIds.size})` : ""}`}
            </BootstrapButton>
          </div>
        </div>
      </Box>
    </Modal>
  );
}